import {
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
  Theme,
  Checkbox,
} from '@mui/material'
import useCustomStyles from '../../../utils/hooks/CustomStylesHook'
import {
  getCommentsByCampId,
  getFollowUpActions,
  postCommentsByCampId,
  updateCommentsByCampId,
  updateCampStatus,
} from '../../../services/CampManagementService'
import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import ToastMessage from '../../lms/components/ToastMessage'
import { toastOptions } from '../../lms/models'
import { useAppSelector } from '../../../app/hooks'
import moduleStyles from '../listOfCamps.module.scss'

const styles = (theme: Theme) => ({
  customFontBox: {
    '& .MuiTypography-root': {
      fontFamily: 'Noto Sans, sans-serif',
      color: '#363463',
    },
    '& .MuiButton-root': {
      fontFamily: 'Open Sans, sans-serif',
      fontSize: '12px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      textTransform: 'capitalize',
      width: '147px',
      margin: '2px',
      justifyContent: 'center',
      color: '#6c757d',
    },
    '& .MuiButton-contained': {
      backgroundColor: '#4682b4 !important',
      color: '#f1f3f5 !important',
      fontWeight: 600,
      border: '1px solid #4682b4 !important',
    },
  },
  gradeContainer: {
    padding: '16px',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    backgroundColor: '#f9f9f9',
    marginBottom: '24px',
  },
  gradeLabel: {
    marginBottom: '16px',
    fontWeight: 'bold',
    fontSize: '1rem',
    color: 'inherit',
  },
  radioGroup: {
    marginBottom: '16px',
    '& .MuiFormControlLabel-root': {
      marginRight: '24px',
      '&:last-child': {
        marginRight: 0,
      },
    },
  },
  radioLabel: {
    '& .MuiSvgIcon-root': {
      fontSize: '20px',
    },
    '& .MuiFormControlLabel-label': {
      fontSize: '0.9rem',
    },
  },
  notesField: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.12)',
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(0, 0, 0, 0.6)',
    },
  },
  textFieldStyle: {
    '& .MuiInputBase-input': {
      fontSize: '12px',
      width: '100%',
      padding: '4px',
    },
  },
  textFieldWhiteBg: {
    backgroundColor: '#ffffff',
    width: '400px !important',
    maxWidth: '100%',
  },
  clearButton: {
    textTransform: 'none',
    fontSize: '0.75rem',
    color: '#4682b4',
    padding: '4px 8px',
    '&:hover': {
      backgroundColor: 'rgba(70, 130, 180, 0.04)',
    },
  },
  fieldLabel: {
    textAlign: 'right',
    fontSize: '12px',
    minWidth: '250px',
    paddingRight: '16px',
    alignSelf: 'flex-start',
    paddingTop: '8px',
    [theme?.breakpoints?.between('sm', 'md')]: {
      minWidth: '228px',
    },
    [theme?.breakpoints?.up('md')]: {
      minWidth: '250px',
    },
  },
  submitButtonContainer: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    mt: 2,
  },
})

const CommentsSection = ({
  coordinatorComments,
  doctorComments,
  setCoordinatorComments,
  setDoctorComments,
  onHandleClick,
  followUpActionsList,
  selectedActions,
  onActionChange,
  setSelectedActions,
}: {
  coordinatorComments: string
  doctorComments: string
  setCoordinatorComments: (val: string) => void
  setDoctorComments: (val: string) => void
  onHandleClick: () => void
  followUpActionsList: Array<{ id: string; action: string }>
  selectedActions: string[]
  onActionChange: (action: string) => void
  setSelectedActions: React.Dispatch<React.SetStateAction<string[]>>
}) => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)

  return (
    <Box
      className={`${classes?.customFontBox} doctor-review-box`}
      display={'flex'}
      flexDirection={'column'}
      gap={2}
    >
      {/* Coordinator Comments */}
      <Box
        display={'flex'}
        justifyContent={'start'}
        alignItems={'center'}
        width={'100%'}
        flexWrap={'wrap'}
        gap={1}
        className={moduleStyles.doctorReviewRow}
      >
        <Typography className={classes?.fieldLabel}>
          Coordinator Comments
        </Typography>
        <TextField
          multiline
          rows={3}
          placeholder='Enter Coordinator comments'
          variant='outlined'
          className={`doctor-review-textfield ${classes?.textFieldStyle} ${classes?.textFieldWhiteBg}`}
          fullWidth
          value={coordinatorComments}
          onChange={(e) => setCoordinatorComments(e?.target?.value)}
          InputProps={{
            className: 'doctor-review-input',
          }}
        />
      </Box>
      {/* Doctor Comments */}
      <Box
        display={'flex'}
        justifyContent={'start'}
        alignItems={'center'}
        width={'100%'}
        flexWrap={'wrap'}
        gap={1}
        className={moduleStyles.doctorReviewRow}
      >
        <Typography className={classes?.fieldLabel}>Doctor Comments</Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          variant='outlined'
          placeholder='Enter doctor comments'
          className={`doctor-review-textfield ${classes?.textFieldStyle} ${classes?.textFieldWhiteBg}`}
          value={doctorComments}
          onChange={(e) => setDoctorComments(e?.target?.value)}
          InputProps={{
            className: 'doctor-review-input',
          }}
        />
      </Box>

      {/* Follow-up Actions */}
      <Box
        className={moduleStyles.doctorReviewRow}
        style={{ marginTop: '16px' }}
      >
        <Typography className={classes?.fieldLabel}>
          Follow-up Actions
        </Typography>
        <Box flex={1}>
          <Box display='flex' justifyContent='flex-end' mb={1}>
            {selectedActions.length > 0 && (
              <Button
                size='small'
                onClick={() => setSelectedActions([])}
                className={classes?.clearButton}
              >
                Undo All
              </Button>
            )}
          </Box>
          <Box className={moduleStyles.doctorReviewActionsGrid}>
            {followUpActionsList.length > 0 ? (
              followUpActionsList.map((item) => (
                <Box
                  key={item.id}
                  className={`doctor-review-action-item ${
                    selectedActions.includes(item.action) ? 'selected' : ''
                  }`}
                >
                  <Typography
                    variant='body2'
                    className={moduleStyles.doctorReviewActionText}
                  >
                    {item.action}
                  </Typography>
                  <Checkbox
                    checked={selectedActions.includes(item.action)}
                    onChange={() => onActionChange(item.action)}
                    value={item.action}
                    size='small'
                    className={moduleStyles.doctorReviewCheckbox}
                  />
                </Box>
              ))
            ) : (
              <Typography
                variant='body2'
                color='textSecondary'
                className={moduleStyles.doctorReviewNoActions}
              >
                No follow-up actions available
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* <Box
        display={'flex'}
        justifyContent={'start'}
        alignItems={'flex-start'}
        width={'100%'}
        flexWrap={'wrap'}
        gap={1}
        className={moduleStyles.doctorReviewRow}
      >
        <Typography className={classes?.fieldLabel}>Grade & Notes</Typography>
        <Box
          className={classes.gradeContainer}
          style={{ flex: 1, maxWidth: '100%' }}
        >
          <FormControl component='fieldset' fullWidth>
            <RadioGroup
              row
              aria-label='grade'
              name='grade-radio-buttons-group'
              value={grade}
              onChange={(e) => setGrade(e?.target?.value)}
              className={classes.radioGroup}
            >
              {['a', 'b', 'c', 'd', 'e']?.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={<Radio size='small' />}
                  label={option?.toUpperCase()}
                  className={classes.radioLabel}
                />
              ))}
            </RadioGroup>

            <TextField
              fullWidth
              margin='normal'
              label='Additional Notes'
              multiline
              rows={3}
              variant='outlined'
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              placeholder='Enter any additional notes here...'
              InputProps={{
                className: 'doctor-review-input',
              }}
              className={`${classes.notesField} ${classes.textFieldStyle}`}
            />
          </FormControl>
        </Box>
      </Box> */}

      <Box className={moduleStyles.doctorReviewSubmitBtnContainer}>
        <Button
          variant='contained'
          color='primary'
          onClick={onHandleClick}
          className={moduleStyles.doctorReviewSubmitBtn}
        >
          Submit
        </Button>
      </Box>
    </Box>
  )
}

const DoctorReview = ({ camp, visitId }: { camp?: any; visitId?: string }) => {
  const { id = visitId } = useParams()
  const [coordinatorComments, setCoordinatorComments] = useState<string>('')
  const [doctorComments, setDoctorComments] = useState<string>('')
  const [commentId, setCommentId] = useState<string>('')
  const [followUpActionsList, setFollowUpActionsList] = useState<
    Array<{ id: string; action: string }>
  >([])
  const [selectedActions, setSelectedActions] = useState<string[]>([])
  const activeCamp = useAppSelector(
    (state) => state?.campManagement?.selectedCamp
  )
  const selectedCamp = activeCamp || camp
  const getComments = useCallback(async () => {
    document.title = 'Doctor Review'
    const resComments = await getCommentsByCampId(id ?? '')
    if (resComments?.success) {
      setCommentId(resComments?.data?.id)
      setCoordinatorComments(resComments?.data?.coordinatorComments ?? '')
      setDoctorComments(resComments?.data?.doctorComments ?? '')
      // Set the selected actions from the API response
      if (resComments?.data?.followUpActions?.length > 0) {
        setSelectedActions(resComments.data.followUpActions)
      } else {
        setSelectedActions([])
      }
    }
  }, [id])

  const getFollowUpActionsList = useCallback(async () => {
    try {
      const resFollowUpActionsList = await getFollowUpActions()
      if (resFollowUpActionsList?.success) {
        setFollowUpActionsList(resFollowUpActionsList.data || [])
      }
    } catch (error) {
      toast.error(`Error fetching follow-up actions: ${error}`)
    }
  }, [])

  useEffect(() => {
    getComments()
    getFollowUpActionsList()
  }, [getComments, getFollowUpActionsList])

  const updateComments = async () => {
    const payload = {
      id: commentId,
      campId: selectedCamp?.campId,
      visitId: id,
      doctorComments,
      coordinatorComments,
      followUpActions: selectedActions,
    }
    const resUpdate = await updateCommentsByCampId(payload)
    if (resUpdate?.success) {
      if (doctorComments?.trim() && id) {
        await updateCampStatusWithComments(id)
      }
      toast(<ToastMessage message={resUpdate?.message} />, {
        ...toastOptions,
        type: 'success',
      })
    } else {
      toast(<ToastMessage message={resUpdate?.message} />, {
        ...toastOptions,
        type: 'error',
      })
    }
    getComments()
  }

  const updateCampStatusWithComments = async (visitId: string) => {
    if (!visitId) {
      return false
    }

    try {
      await updateCampStatus(visitId, 'finished')
      return true
    } catch (error) {
      toast(
        <ToastMessage message='Comments saved but failed to update status' />,
        {
          ...toastOptions,
          type: 'error',
        }
      )
      return false
    }
  }

  const sendComments = async () => {
    try {
      const payload = {
        campId: selectedCamp?.campId,
        visitId: id,
        doctorComments,
        coordinatorComments,
        followUpActions: selectedActions,
      }

      // First, post the comments
      const resSendComments = await postCommentsByCampId(payload)

      if (resSendComments?.success) {
        // Update camp status if there are doctor comments
        if (doctorComments?.trim() && id) {
          await updateCampStatusWithComments(id)
        }

        toast(<ToastMessage message={resSendComments?.message} />, {
          ...toastOptions,
          type: 'success',
        })
      } else {
        toast(<ToastMessage message={resSendComments?.message} />, {
          ...toastOptions,
          type: 'error',
        })
      }
      getComments()
    } catch (error) {
      toast(<ToastMessage message='Failed to save comments' />, {
        ...toastOptions,
        type: 'error',
      })
    }
  }

  return (
    <Box className={moduleStyles.doctorReviewContainer}>
      <Box className={moduleStyles.doctorReviewContent}>
        <CommentsSection
          coordinatorComments={coordinatorComments}
          doctorComments={doctorComments}
          setCoordinatorComments={setCoordinatorComments}
          setDoctorComments={setDoctorComments}
          onHandleClick={commentId ? updateComments : sendComments}
          followUpActionsList={followUpActionsList}
          selectedActions={selectedActions}
          onActionChange={(action) => {
            setSelectedActions((prev) =>
              prev.includes(action)
                ? prev.filter((a) => a !== action)
                : [...prev, action]
            )
          }}
          setSelectedActions={setSelectedActions}
        />
      </Box>
    </Box>
  )
}

export default DoctorReview
